import { CutSceneStepAction } from "../actions";

/**
 * One step of a cinematic script. A step only lists what it changes;
 * `CUT_SCENE_DEFAULTS` supplies the rest. `$` inside `txt` starts a new line.
 */
export interface CutSceneStep {
  readonly txt?: string;
  /** Frames the step lasts before the next one starts. */
  readonly frameLimit?: number;
  /** Side effect for `CutScene.update` to run when the step starts. */
  readonly action?: CutSceneStepAction;
}

/** Values used by every step that does not override them. */
export const CUT_SCENE_DEFAULTS = {
  txt: "",
  frameLimit: 5,
  action: CutSceneStepAction.None,
} as const;

/**
 * Every scene is padded out to this many steps; the original built a fixed
 * 25-entry script and left the tail of each scene on the defaults.
 */
export const CUT_SCENE_STEPS = 25;

/** The opening (0) and ending (1) cinematics, indexed by `CutScene`'s `sceneType`. */
export const CUT_SCENES: readonly (readonly CutSceneStep[])[] = [
  // 0: opening
  [
    /*  0 */ { frameLimit: 50 },
    /*  1 */ { frameLimit: 25, action: CutSceneStepAction.StartEndingMusic },
    /*  2 */ {
      txt: "In the year 2115, a group of scientists discovered a new form of energy, beneath the Earth's oceans. Capable of powering advanced technology, they called it: Xenos.",
      frameLimit: 300,
      action: CutSceneStepAction.AddGeneratorEffect,
    },
    /*  3 */ { frameLimit: 30, action: CutSceneStepAction.HideCover },
    /*  4 */ {
      txt: "One of the scientists, Dr Crenson, seized control of the Xenos energy. Using it to power his own private army,$and a gigantic flying fortress from which to command it.",
      frameLimit: 30,
      action: CutSceneStepAction.KillEffects,
    },
    /*  5 */ { frameLimit: 270, action: CutSceneStepAction.ShowFortress },
    /*  6 */ {
      txt: "Soon after, a great mechanical city began to spread from beneath the fortress, consuming all in its path.",
      frameLimit: 50,
    },
    /*  7 */ { frameLimit: 250, action: CutSceneStepAction.StartStorm },
    /*  8 */ { frameLimit: 15, action: CutSceneStepAction.RevealCrenson },
    /*  9 */ {
      txt: "Dr Crenson's army appeared unstoppable, powered by the energy of Xenos. Slowly the Earth's major conurbations began to fall under his might.",
      frameLimit: 300,
    },
    /* 10 */ { frameLimit: 50, action: CutSceneStepAction.ClearText },
    /* 11 */ {
      frameLimit: 30,
      action: CutSceneStepAction.EndStormAndShowCover,
    },
    /* 12 */ {
      txt: "However, all was not lost. A group of people, refusing to bend to Crenson's regime, managed to steal one of the Xenos generators, located on the outskirts of Eutopia.",
      frameLimit: 40,
      action: CutSceneStepAction.AddCoreEffect,
    },
    /* 13 */ { frameLimit: 220, action: CutSceneStepAction.SwitchToCircuit },
    /* 14 */ {
      frameLimit: 40,
      action: CutSceneStepAction.LaunchBlueprintEffect,
    },
    /* 15 */ {
      txt: "Together, they forged mankind's last hope of resistance.$A battle-suit, powered by Xenos, capable of standing up to Crenson and his army.",
      frameLimit: 100,
      action: CutSceneStepAction.BuildRobotBlueprint,
    },
    /* 16 */ { frameLimit: 200, action: CutSceneStepAction.ShowSecondObject },
    /* 17 */ { frameLimit: 40, action: CutSceneStepAction.HideObjects },
    /* 18 */ { frameLimit: 40, action: CutSceneStepAction.SwitchToTunnel },
    /* 19 */ { frameLimit: 40, action: CutSceneStepAction.LaunchRobot },
    /* 20 */ {
      txt: "You are the pilot.$Now is the time.$Defeat Dr Crenson, and save humanity!",
      frameLimit: 260,
    },
    /* 21 */ { frameLimit: 40, action: CutSceneStepAction.AddRobotShine },
    /* 22 */ { frameLimit: 5000, action: CutSceneStepAction.FinishScene },
  ],
  // 1: ending
  [
    /*  0 */ { frameLimit: 25 },
    /*  1 */ { frameLimit: 60, action: CutSceneStepAction.HideEndingCover },
    /*  2 */ {
      txt: "Asterus watched from afar as the fortress crumbled.",
      frameLimit: 300,
    },
    /*  3 */ { frameLimit: 150, action: CutSceneStepAction.DestroyFortress },
    /*  4 */ { frameLimit: 70, action: CutSceneStepAction.ShowEndingCover },
    // the AS3 had two `e == 5` branches here; the second overwrote frameLimit 100 with 175 and added the action
    /*  5 */ {
      txt: "Dr Crenson was defeated,$but his legacy was not yet over.",
      frameLimit: 175,
      action: CutSceneStepAction.AddFinalBossEffect,
    },
    /*  6 */ {
      txt: "The Xenos Core had split into pieces$and scattered itself across the land.",
      frameLimit: 200,
    },
    /*  7 */ { frameLimit: 100, action: CutSceneStepAction.KillEndingEffects },
    /*  8 */ { frameLimit: 60, action: CutSceneStepAction.SwitchToSunset },
    /*  9 */ {
      txt: "Will Asterus have to fight again?$Perhaps, but for now his work is over.",
      frameLimit: 300,
    },
    /* 10 */ { frameLimit: 90, action: CutSceneStepAction.SetCyanText },
    /* 11 */ {
      txt: "Programming, Art and Design$       Thomas Jenns",
      frameLimit: 300,
    },
    /* 12 */ {
      txt: "Music$       Lem Donaldson & Thomas Jenns",
      frameLimit: 300,
    },
    /* 13 */ { txt: "GUI Art$       Lem Donaldson", frameLimit: 300 },
    /* 14 */ { txt: "Managing Director$       Jesse Roberts", frameLimit: 300 },
    /* 15 */ { frameLimit: 400, action: CutSceneStepAction.ClearEndingText },
    /* 16 */ { frameLimit: 75, action: CutSceneStepAction.StartRobotFlyaway },
    /* 17 */ { frameLimit: 1000, action: CutSceneStepAction.FinishEnding },
  ],
];
